import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Users } from 'lucide-react';
import { Button as UIButton } from './ui/button';
import { Label as UILabel } from './ui/label';
import { Checkbox as UICheckbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';


const API = "/api";

const ProductPermissionsModal = ({ product, isOpen, onClose, onUpdate }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [showPermissionForm, setShowPermissionForm] = useState(false);
  const [permissionForm, setPermissionForm] = useState({ user_id: '', permissions: [] });
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [productPermissions, setProductPermissions] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);

  useEffect(() => {
    if (isOpen && product) {
      fetchProductPermissions();
      fetchAvailableUsers();
    }
  }, [isOpen, product]);

  const fetchProductPermissions = async () => {
    if (!product) return;
    
    try {
      const response = await axios.get(`${API}/products/${product.id}/permissions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
        }
      });
      const permissions = Array.isArray(response.data) ? response.data : [];
      setProductPermissions(permissions);
    } catch (error) {
      console.error('Failed to fetch product permissions:', error.response?.data || error);
      setProductPermissions([]);
      toast({
        title: "Error",
        description: "Failed to load product permissions",
        variant: "destructive",
      });
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
        }
      });
      const users = Array.isArray(response.data) ? response.data : [];
      setAvailableUsers(users);
    } catch (error) {
      console.error('Failed to fetch available users:', error);
      setAvailableUsers([]);
      toast({
        title: "Error",
        description: "Failed to load available users",
        variant: "destructive",
      });
    }
  };

  const handlePermissionSubmit = async (e) => {
    e.preventDefault();
    
    if (!permissionForm.user_id) {
      toast({ title: "Error", description: "Please select a user", variant: "destructive" });
      return;
    }
    
    if (!permissionForm.permissions || permissionForm.permissions.length === 0) {
      toast({ title: "Error", description: "Please select at least one permission", variant: "destructive" });
      return;
    }
    
    const validPermissions = ['VIEW', 'EDIT', 'MANAGE'];
    const invalidPermissions = permissionForm.permissions.filter(p => !validPermissions.includes(p));
    if (invalidPermissions.length > 0) {
      toast({ title: "Error", description: "Invalid permission values detected", variant: "destructive" });
      return;
    }
    
    setPermissionLoading(true);
    try {
      await axios.post(
        `${API}/products/${product.id}/permissions`,
        {
          user_id: permissionForm.user_id,
          permissions: permissionForm.permissions
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        }
      );
      
      toast({ title: "Success", description: "User permission added successfully" });
      
      setPermissionForm({ user_id: '', permissions: [] });
      setShowPermissionForm(false);
      fetchProductPermissions();
      
    } catch (error) {
      console.error('Permission creation error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to add user permission";
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    } finally {
      setPermissionLoading(false);
    }
  };

  const handleRemovePermission = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user\'s permissions?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/products/${product.id}/permissions/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
        }
      });
      
      toast({ title: "Success", description: "User permission removed successfully" });
      fetchProductPermissions();
      
    } catch (error) {
      toast({ title: "Error", description: "Failed to remove user permission", variant: "destructive" });
    }
  };

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Manage Permissions - {product.title}</h2>
            <p className="text-sm text-slate-600">Control user access to this product</p>
          </div>
          <UIButton variant="ghost" size="sm" onClick={onClose} className="text-slate-500 hover:text-slate-700">
            <X className="w-5 h-5" />
          </UIButton>
        </div>

        <div className="p-6 overflow-y-auto flex-grow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">User Permissions</h3>
            <UIButton onClick={() => setShowPermissionForm(!showPermissionForm)} size="sm" className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              {showPermissionForm ? 'Cancel' : 'Add Permission'}
            </UIButton>
          </div>

          {showPermissionForm && (
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-slate-900 mb-4">Add User Permission</h4>
              <form onSubmit={handlePermissionSubmit} className="space-y-4">
                <div>
                  <UILabel htmlFor="user_select">User *</UILabel>
                  <select
                    id="user_select"
                    value={permissionForm.user_id}
                    onChange={(e) => setPermissionForm({...permissionForm, user_id: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select a user...</option>
                    {Array.isArray(availableUsers) 
                      ? availableUsers
                        .filter(u => u.id !== user.id) // Exclude current user
                        .map(u => (
                          <option key={u.id} value={u.id}>
                            {u.full_name || u.username} ({u.username})
                          </option>
                        ))
                      : <option disabled>No users available</option>
                    }
                  </select>
                </div>

                <div>
                  <UILabel>Permissions *</UILabel>
                  <div className="space-y-2 mt-2">
                    {['VIEW', 'EDIT', 'MANAGE'].map(permission => (
                      <div key={permission} className="flex items-center space-x-2">
                        <UICheckbox
                          id={`permission_${permission}`}
                          checked={permissionForm.permissions.includes(permission)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setPermissionForm(prev => ({...prev, permissions: [...prev.permissions, permission]}));
                            } else {
                              setPermissionForm(prev => ({...prev, permissions: prev.permissions.filter(p => p !== permission)}));
                            }
                          }}
                        />
                        <UILabel htmlFor={`permission_${permission}`} className="text-sm font-normal">
                          {permission.charAt(0) + permission.slice(1).toLowerCase()}
                        </UILabel>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  <UIButton type="button" variant="outline" onClick={() => { setPermissionForm({ user_id: '', permissions: [] }); setShowPermissionForm(false); }} disabled={permissionLoading}>Cancel</UIButton>
                  <UIButton type="submit" disabled={permissionLoading || !permissionForm.user_id || permissionForm.permissions.length === 0}>
                    {permissionLoading ? 'Adding...' : 'Add Permission'}
                  </UIButton>
                </div>
              </form>
            </div>
          )}

          {productPermissions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No user permissions configured for this product</p>
              <p className="text-sm">Click "Add Permission" to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {Array.isArray(productPermissions) && productPermissions.map(permission => {
                const pUser = Array.isArray(availableUsers) ? availableUsers.find(u => u.id === permission.user_id) : null;
                return (
                  <div key={permission.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-slate-900">{pUser ? (pUser.full_name || pUser.username) : permission.user_id}</h4>
                          {pUser && <Badge variant="outline" className="text-xs">{pUser.username}</Badge>}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {permission.permissions.map(p => (
                            <Badge key={p} variant="secondary" className="text-xs">{p.toLowerCase()}</Badge>
                          ))}
                        </div>
                        <div className="text-xs text-slate-500 mt-2">
                          Granted by: {permission.granted_by_name || permission.granted_by} • {new Date(permission.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <UIButton variant="outline" size="sm" onClick={() => handleRemovePermission(permission.user_id)} className="flex items-center gap-2 text-red-600 hover:text-red-700 ml-4">
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </UIButton>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductPermissionsModal;
